import { useState } from 'react'
import { useMe } from '../hooks/useMe'
import { useArchived } from '../contexts/ArchiveContext'
import {
  useNotes, useCreateNote, usePatchNote, usePinNote, useUnpinNote, useDeleteNote
} from '../hooks/useNotes'
import type { EventNote } from '../types'

// NotesTab - per-event decisions / context log. Compose box at the top, feed
// below (pinned first, then chronological). Markdown-rich body is deferred
// to a later iteration; v1 renders body with whitespace-pre-wrap so line
// breaks survive.

interface Props {
  eventId: number
}

export function NotesTab({ eventId }: Props) {
  const archived = useArchived()
  const { data: me } = useMe()
  const { data, isLoading, error } = useNotes(eventId)
  const createMut = useCreateNote(eventId)
  const patchMut = usePatchNote(eventId)
  const pinMut = usePinNote(eventId)
  const unpinMut = useUnpinNote(eventId)
  const deleteMut = useDeleteNote(eventId)

  const [body, setBody] = useState('')
  const [pinAsDecision, setPinAsDecision] = useState(false)
  const [pinnedLabel, setPinnedLabel] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingBody, setEditingBody] = useState('')

  function handlePost() {
    if (!body.trim()) return
    createMut.mutate(
      {
        body,
        isPinned: pinAsDecision || undefined,
        pinnedLabel: pinAsDecision && pinnedLabel.trim() ? pinnedLabel.trim() : null
      },
      {
        onSuccess: () => {
          setBody('')
          setPinAsDecision(false)
          setPinnedLabel('')
        }
      }
    )
  }

  function handleKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handlePost()
    }
  }

  function startEditing(note: EventNote) {
    setEditingId(note.id)
    setEditingBody(note.body)
  }

  function saveEdit() {
    if (editingId == null) return
    patchMut.mutate(
      { noteId: editingId, body: { body: editingBody } },
      { onSuccess: () => setEditingId(null) }
    )
  }

  function handleDelete(note: EventNote) {
    const preview = note.body.length > 40 ? note.body.slice(0, 40) + '…' : note.body
    const ok = window.confirm(`Delete this note? "${preview}"`)
    if (!ok) return
    deleteMut.mutate(note.id)
  }

  function togglePin(note: EventNote) {
    if (note.isPinned) {
      unpinMut.mutate(note.id)
    } else {
      const label = window.prompt(
        'Short label for this decision (shown in PDF, e.g. "Loco confirmed"). Leave blank to omit.',
        ''
      )
      if (label === null) return    // user cancelled
      pinMut.mutate({ noteId: note.id, pinnedLabel: label.trim() || null })
    }
  }

  if (isLoading) {
    return <div className="text-ink-soft text-sm font-mono">Loading notes…</div>
  }
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load notes</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }

  const notes = data ?? []

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl">Notes &amp; decisions</h2>
        <p className="text-sm text-ink-soft mt-0.5">
          Freeform log of decisions, context, and conversations. Pin an entry
          to surface it in the event PDF as a decision.
        </p>
      </div>

      {/* Composer */}
      {!archived && (
      <div className="bg-white border border-line rounded p-4 space-y-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={handleKey}
          rows={3}
          placeholder="What changed, what was decided, what to remember… (⌘+Enter to post)"
          className="w-full border border-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:border-green resize-none"
        />
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-ink-soft cursor-pointer">
            <input
              type="checkbox"
              checked={pinAsDecision}
              onChange={(e) => setPinAsDecision(e.target.checked)}
              className="accent-green"
            />
            Pin as decision
          </label>
          {pinAsDecision && (
            <input
              type="text"
              value={pinnedLabel}
              onChange={(e) => setPinnedLabel(e.target.value)}
              placeholder="Short label (optional, shown in PDF)"
              maxLength={120}
              className="flex-1 max-w-xs border border-line rounded px-2.5 py-1 text-xs"
            />
          )}
          <button
            type="button"
            onClick={handlePost}
            disabled={!body.trim() || createMut.isPending}
            className="ml-auto px-3 py-1.5 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {createMut.isPending ? 'Posting…' : 'Post'}
          </button>
        </div>
        {createMut.error && (
          <p className="text-xs text-oxblood">
            Could not post: {(createMut.error as Error).message}
          </p>
        )}
      </div>
      )}

      {/* Feed */}
      {notes.length === 0 ? (
        <div className="bg-white border border-line rounded p-8 text-center text-sm text-ink-soft italic">
          No notes yet. Start the log with the first decision.
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => {
            const canEdit = (!!me?.isAdmin || me?.dbUserId === note.author.id) && !archived
            const isEditing = editingId === note.id && !archived
            return (
              <div
                key={note.id}
                className={
                  'bg-white border rounded p-4 ' +
                  (note.isPinned ? 'border-brass border-l-4' : 'border-line')
                }
              >
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-green text-parchment text-xs font-semibold flex items-center justify-center flex-shrink-0">
                    {note.author.avatarInitials ?? '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2 mb-1">
                      <span className="text-sm font-semibold">{note.author.name ?? 'Someone'}</span>
                      <span className="text-[11px] text-ink-soft font-mono">
                        {relativeTime(note.createdAt)}
                      </span>
                      {note.editedAt && (
                        <span className="text-[11px] italic text-ink-soft">
                          (edited)
                        </span>
                      )}
                      {note.isPinned && (
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
                          style={{ background: '#f5edd5', color: '#9b7f2a' }}
                        >
                          📌 {note.pinnedLabel ?? 'Decision'}
                        </span>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="space-y-2">
                        <textarea
                          value={editingBody}
                          onChange={(e) => setEditingBody(e.target.value)}
                          rows={4}
                          className="w-full border border-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:border-green resize-none"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={saveEdit}
                            disabled={patchMut.isPending}
                            className="px-3 py-1 rounded text-xs font-semibold bg-green text-parchment hover:bg-green/90 disabled:opacity-50"
                          >
                            {patchMut.isPending ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            className="px-3 py-1 rounded text-xs text-ink-soft hover:bg-parchment"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{note.body}</p>
                    )}
                  </div>
                </div>
                {!isEditing && !archived && (
                  <div className="flex justify-end gap-3 mt-2 text-xs text-ink-soft">
                    <button
                      type="button"
                      onClick={() => togglePin(note)}
                      className="hover:text-brass transition-colors"
                    >
                      {note.isPinned ? 'Unpin' : 'Pin as decision'}
                    </button>
                    {canEdit && (
                      <>
                        <button
                          type="button"
                          onClick={() => startEditing(note)}
                          className="hover:text-green transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(note)}
                          className="hover:text-oxblood transition-colors"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return iso
  const diffSec = Math.round((Date.now() - then) / 1000)
  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} min ago`
  if (diffSec < 86_400) return `${Math.round(diffSec / 3600)} hr ago`
  if (diffSec < 7 * 86_400) return `${Math.round(diffSec / 86_400)} days ago`
  // After a week, switch to absolute date.
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
